import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Package, Truck, Clock, Shield, ArrowRight, Menu, X, ChevronRight, Zap, DollarSign, MapPin, Star, CheckCircle, Award, Box, TrendingDown, Search } from "lucide-react";
import axios from "axios";
import { API, useAuth } from "@/App";
import QuoteResults from "@/components/QuoteResults";
import { CARRIER_LOGOS, CARRIER_COLORS } from "@/components/CarrierLogos";
import ColoniaSearch from "@/components/ColoniaSearch";

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    origin_zip: "", destination_zip: "",
    weight: "", length: "", width: "", height: ""
  });
  const [quotes, setQuotes] = useState(null);
  const [loading, setLoading] = useState(false);
  const resultsRef = useRef(null);
  const [originInfo, setOriginInfo] = useState(null);
  const [destInfo, setDestInfo] = useState(null);
  const [selectedOriginColonia, setSelectedOriginColonia] = useState("");
  const [selectedDestColonia, setSelectedDestColonia] = useState("");
  const [showOriginColoniaSearch, setShowOriginColoniaSearch] = useState(false);
  const [showDestColoniaSearch, setShowDestColoniaSearch] = useState(false);

  const lookupZip = async (cp, setter) => {
    if (cp.length === 5) {
      try {
        const res = await axios.get(`${API}/zipcode/${cp}`);
        if (res.data.colonias?.length > 0) setter(res.data);
        else setter(null);
      } catch { setter(null); }
    } else {
      setter(null);
    }
  };

  const handleOriginChange = (val) => {
    setQuoteForm(f => ({...f, origin_zip: val}));
    setSelectedOriginColonia("");
    lookupZip(val, setOriginInfo);
  };
  const handleDestChange = (val) => {
    setQuoteForm(f => ({...f, destination_zip: val}));
    setSelectedDestColonia("");
    lookupZip(val, setDestInfo);
  };

  const handleQuote = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const headers = {};
      const token = localStorage.getItem("token");
      if (token) headers["Authorization"] = `Bearer ${token}`;
      
      const response = await axios.post(`${API}/quotes/calculate`, {
        origin_zip: quoteForm.origin_zip,
        destination_zip: quoteForm.destination_zip,
        weight: parseFloat(quoteForm.weight),
        length: parseFloat(quoteForm.length),
        width: parseFloat(quoteForm.width),
        height: parseFloat(quoteForm.height)
      }, { headers });
      setQuotes(response.data);
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    } catch (error) {
      console.error("Quote error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectQuote = (quote) => {
    if (!user) {
      localStorage.setItem("pendingQuote", JSON.stringify({ quote, quoteDetails: quotes }));
      navigate("/register");
      return;
    }
    navigate("/enviar", { state: { quote, quoteDetails: quotes } });
  };

  const carriers = [
    { name: "FedEx" },
    { name: "DHL" },
    { name: "Estafeta" },
    { name: "UPS" },
    { name: "Redpack" },
    { name: "99 Minutos" },
    { name: "Paquetexpress" },
  ];

  const [imgErrors, setImgErrors] = useState({});
  
  const handleImgError = (carrier) => {
    setImgErrors(prev => ({ ...prev, [carrier]: true }));
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-lg border-b border-zinc-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/25">
                <Package className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold font-['Outfit']">
                <span className="text-violet-600">Super</span>
                <span className="text-zinc-800">Pack</span>
              </span>
              <span className="text-sm text-zinc-400">.mx</span>
            </div>
            
            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              <a href="#beneficios" className="text-zinc-600 hover:text-violet-600 transition-colors font-medium">Beneficios</a>
              <a href="#como-funciona" className="text-zinc-600 hover:text-violet-600 transition-colors font-medium">¿Cómo funciona?</a>
              <a href="/sobre-nosotros" className="text-zinc-600 hover:text-violet-600 transition-colors font-medium">Nosotros</a>
              <a href="/contacto" className="text-zinc-600 hover:text-violet-600 transition-colors font-medium">Contacto</a>
            </nav>

            <div className="hidden md:flex items-center gap-4">
              {user ? (
                <Button 
                  onClick={() => navigate("/dashboard")}
                  className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 px-6 shadow-lg shadow-violet-500/25"
                  data-testid="go-to-dashboard-btn"
                >
                  Mi Dashboard
                </Button>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate("/login")}
                    data-testid="login-btn"
                    className="font-medium"
                  >
                    Iniciar sesión
                  </Button>
                  <Button 
                    onClick={() => navigate("/register")}
                    className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 px-6 shadow-lg shadow-violet-500/25"
                    data-testid="register-btn"
                  >
                    Registrate gratis
                  </Button>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-zinc-200 py-4 px-4">
            <nav className="flex flex-col gap-4">
              <a href="#beneficios" className="text-zinc-600 py-2 font-medium">Beneficios</a>
              <a href="#como-funciona" className="text-zinc-600 py-2 font-medium">¿Cómo funciona?</a>
              <a href="/sobre-nosotros" className="text-zinc-600 py-2 font-medium">Nosotros</a>
              <a href="/contacto" className="text-zinc-600 py-2 font-medium">Contacto</a>
              <hr className="my-2" />
              {user ? (
                <Button onClick={() => navigate("/dashboard")} className="bg-violet-600">Mi Dashboard</Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => navigate("/login")}>Iniciar sesión</Button>
                  <Button onClick={() => navigate("/register")} className="bg-violet-600">Registrate gratis</Button>
                </>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background with image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80"
            alt="Delivery"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-violet-600/20 via-transparent to-orange-500/20"></div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-96 h-96 bg-violet-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-orange-500/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-violet-500/5 to-purple-500/5 rounded-full blur-3xl"></div>
        
        <div className="relative py-8 sm:py-16 lg:py-24 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-center">
              {/* Left - Text */}
              <div className="space-y-4 sm:space-y-8 animate-fade-in">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-1.5 sm:px-5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold shadow-lg">
                  <TrendingDown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  Ahorra hasta 70% en tus envíos
                </div>
                
                <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold font-['Outfit'] tracking-tight text-zinc-900 leading-tight">
                  El mejor precio<br />
                  <span className="bg-gradient-to-r from-violet-600 via-purple-600 to-violet-600 bg-clip-text text-transparent">
                    garantizado
                  </span>
                </h1>
                
                <p className="text-sm sm:text-lg lg:text-xl text-zinc-600 max-w-lg leading-relaxed">
                  Comparamos precios de <strong>FedEx, DHL, Estafeta, UPS</strong> y más paqueterías 
                  para encontrarte siempre el <strong>mejor precio</strong>.
                </p>

                {/* Trust badges - inline on mobile */}
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  <div className="flex items-center gap-1.5 bg-white px-3 py-2 sm:px-4 sm:py-3 rounded-lg sm:rounded-xl shadow-md border border-zinc-100">
                    <Shield className="w-4 h-4 text-emerald-500" />
                    <span className="text-xs sm:text-sm font-medium">Asegurados</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-white px-3 py-2 sm:px-4 sm:py-3 rounded-lg sm:rounded-xl shadow-md border border-zinc-100">
                    <Zap className="w-4 h-4 text-orange-500" />
                    <span className="text-xs sm:text-sm font-medium">Guías al instante</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-white px-3 py-2 sm:px-4 sm:py-3 rounded-lg sm:rounded-xl shadow-md border border-zinc-100">
                    <Star className="w-4 h-4 text-yellow-500" />
                    <span className="text-xs sm:text-sm font-medium">+10,000 clientes</span>
                  </div>
                </div>

                {/* Stats - compact on mobile */}
                <div className="flex gap-5 sm:gap-8 pt-1 sm:pt-4">
                  <div>
                    <p className="text-2xl sm:text-4xl font-bold text-violet-600">7+</p>
                    <p className="text-xs sm:text-sm text-zinc-500">Paqueterías</p>
                  </div>
                  <div className="border-l border-zinc-200 pl-5 sm:pl-8">
                    <p className="text-2xl sm:text-4xl font-bold text-emerald-500">70%</p>
                    <p className="text-xs sm:text-sm text-zinc-500">Ahorro máx.</p>
                  </div>
                  <div className="border-l border-zinc-200 pl-5 sm:pl-8">
                    <p className="text-2xl sm:text-4xl font-bold text-orange-500">24/7</p>
                    <p className="text-xs sm:text-sm text-zinc-500">Disponible</p>
                  </div>
                </div>
              </div>

              {/* Right - Quote Form */}
              <div className="relative">
                <Card className="bg-white border-0 shadow-2xl shadow-violet-500/20" data-testid="quote-form-card">
                  <CardContent className="p-5 sm:p-8">
                    <div className="flex items-center gap-3 mb-4 sm:mb-6">
                      <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-violet-600 to-purple-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg">
                        <Box className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                      </div>
                      <div>
                        <h2 className="text-lg sm:text-2xl font-bold font-['Outfit']">Cotiza ahora</h2>
                        <p className="text-xs sm:text-sm text-zinc-500">Encuentra el mejor precio en segundos</p>
                      </div>
                    </div>
                    
                    <form onSubmit={handleQuote} className="space-y-4 sm:space-y-5">
                      {/* Colonia search - full width when active */}
                      {(showOriginColoniaSearch || showDestColoniaSearch) && (
                        <div className="space-y-1">
                          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider flex items-center gap-1">
                            <Search className="w-3 h-3 text-violet-500" />
                            {showOriginColoniaSearch ? "Buscar CP Origen por ciudad o colonia" : "Buscar CP Destino por ciudad o colonia"}
                          </label>
                          <ColoniaSearch
                            placeholder="Ej: Mérida, Del Valle, Polanco, Guadalajara..."
                            onSelect={(item) => {
                              if (showOriginColoniaSearch) {
                                handleOriginChange(item.cp);
                                setShowOriginColoniaSearch(false);
                              } else {
                                handleDestChange(item.cp);
                                setShowDestColoniaSearch(false);
                              }
                            }}
                            onClose={() => {
                              setShowOriginColoniaSearch(false);
                              setShowDestColoniaSearch(false);
                            }}
                          />
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-3 sm:gap-4">
                        <div>
                          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5 block flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-violet-500" /> CP Origen
                          </label>
                          <Input
                            type="text"
                            placeholder="06600"
                            value={quoteForm.origin_zip}
                            onChange={(e) => handleOriginChange(e.target.value)}
                            required
                            maxLength={5}
                            className="h-11 sm:h-14 text-base sm:text-lg font-medium border-2 focus:border-violet-500"
                            data-testid="origin-zip-input"
                          />
                          <button type="button" onClick={() => { setShowOriginColoniaSearch(true); setShowDestColoniaSearch(false); }}
                            className="text-[11px] text-violet-600 hover:text-violet-800 mt-1 flex items-center gap-1 font-medium"
                            data-testid="origin-search-colonia-btn">
                            <Search className="w-3 h-3" /> Buscar por ciudad
                          </button>
                        </div>
                        <div>
                          <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5 block flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-orange-500" /> CP Destino
                          </label>
                          <Input
                            type="text"
                            placeholder="44100"
                            value={quoteForm.destination_zip}
                            onChange={(e) => handleDestChange(e.target.value)}
                            required
                            maxLength={5}
                            className="h-11 sm:h-14 text-base sm:text-lg font-medium border-2 focus:border-violet-500"
                            data-testid="destination-zip-input"
                          />
                          <button type="button" onClick={() => { setShowDestColoniaSearch(true); setShowOriginColoniaSearch(false); }}
                            className="text-[11px] text-violet-600 hover:text-violet-800 mt-1 flex items-center gap-1 font-medium"
                            data-testid="dest-search-colonia-btn">
                            <Search className="w-3 h-3" /> Buscar por ciudad
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5 block">
                          Peso (kg)
                        </label>
                        <Input
                          type="number"
                          placeholder="2.5"
                          value={quoteForm.weight}
                          onChange={(e) => setQuoteForm({...quoteForm, weight: e.target.value})}
                          required
                          min="0.1"
                          step="0.1"
                          className="h-11 sm:h-14 text-base sm:text-lg font-medium border-2 focus:border-violet-500"
                          data-testid="weight-input"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-1.5 block">
                          Dimensiones (cm)
                        </label>
                        <div className="grid grid-cols-3 gap-2 sm:gap-3">
                          <Input
                            type="number"
                            placeholder="Largo"
                            value={quoteForm.length}
                            onChange={(e) => setQuoteForm({...quoteForm, length: e.target.value})}
                            required
                            min="1"
                            className="h-11 sm:h-14 border-2 focus:border-violet-500 text-sm sm:text-base"
                            data-testid="length-input"
                          />
                          <Input
                            type="number"
                            placeholder="Ancho"
                            value={quoteForm.width}
                            onChange={(e) => setQuoteForm({...quoteForm, width: e.target.value})}
                            required
                            min="1"
                            className="h-11 sm:h-14 border-2 focus:border-violet-500 text-sm sm:text-base"
                            data-testid="width-input"
                          />
                          <Input
                            type="number"
                            placeholder="Alto"
                            value={quoteForm.height}
                            onChange={(e) => setQuoteForm({...quoteForm, height: e.target.value})}
                            required
                            min="1"
                            className="h-11 sm:h-14 border-2 focus:border-violet-500 text-sm sm:text-base"
                            data-testid="height-input"
                          />
                        </div>
                      </div>

                      <Button 
                        type="submit" 
                        className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 h-12 sm:h-16 text-base sm:text-lg font-bold shadow-xl shadow-violet-500/30 rounded-xl"
                        disabled={loading}
                        data-testid="quote-submit-btn"
                      >
                        {loading ? (
                          <div className="flex items-center gap-3">
                            <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                            Consultando paqueterías...
                          </div>
                        ) : (
                          <span className="flex items-center gap-2">
                            Cotizar ahora <ArrowRight className="w-6 h-6" />
                          </span>
                        )}
                      </Button>

                      {/* Loading overlay */}
                      {loading && (
                        <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 text-center animate-pulse" data-testid="quote-loading-message">
                          <div className="flex items-center justify-center gap-3 mb-2">
                            <Truck className="w-5 h-5 text-violet-600 animate-bounce" />
                            <span className="font-semibold text-violet-700 text-sm">Estamos consultando las mejores paqueterías</span>
                          </div>
                          <p className="text-xs text-violet-500">En breve obtendrás las mejores tarifas para tu envío...</p>
                        </div>
                      )}
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Quote Results */}
            {quotes && (
              <div className="mt-16 animate-fade-in" ref={resultsRef}>
                <QuoteResults quotes={quotes} showActions={true} onSelect={handleSelectQuote} />
                {!user && (
                  <div className="mt-8 bg-violet-50 border border-violet-200 rounded-2xl p-6 text-center">
                    <p className="text-violet-700 font-medium">
                      Regístrate gratis para generar tu guía al instante
                    </p>
                    <Button 
                      onClick={() => navigate("/register")} 
                      className="mt-3 bg-violet-600 hover:bg-violet-700"
                      data-testid="register-cta-btn"
                    >
                      Crear cuenta gratis <ChevronRight className="w-5 h-5 ml-1" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Carriers Section */}
      <section id="paqueterias" className="py-12 sm:py-20 bg-gradient-to-b from-zinc-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8 sm:mb-12">
            <span className="text-violet-600 font-semibold text-xs sm:text-sm uppercase tracking-wider">Paqueterías</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Outfit'] mt-2">
              Las mejores paqueterías de México
            </h2>
            <p className="text-zinc-500 mt-2 text-sm sm:text-base">Encontramos el mejor precio para ti en cada una</p>
          </div>
          
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3 sm:gap-6">
            {carriers.map((carrier, index) => {
              const logoUrl = CARRIER_LOGOS[carrier.name];
              const color = CARRIER_COLORS[carrier.name] || "#333";
              const hasError = imgErrors[carrier.name];
              
              return (
                <div 
                  key={carrier.name}
                  className="bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 p-3 sm:p-6 flex flex-col items-center justify-center aspect-square border border-zinc-100 animate-fade-in group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-16 h-10 sm:w-24 sm:h-16 flex items-center justify-center">
                    {logoUrl && !hasError ? (
                      <img 
                        src={logoUrl}
                        alt={`Logo ${carrier.name}`}
                        className="max-h-full max-w-full object-contain group-hover:scale-110 transition-transform"
                        onError={() => handleImgError(carrier.name)}
                      />
                    ) : (
                      <span 
                        className="font-bold text-lg text-center"
                        style={{ color }}
                      >
                        {carrier.name}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="beneficios" className="py-12 sm:py-24 px-4 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full">
          <img 
            src="https://images.unsplash.com/photo-1769355104335-acef3aa4c9b6?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80"
            alt="Warehouse"
            className="w-full h-full object-cover opacity-10"
          />
        </div>
        
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div>
              <span className="text-violet-600 font-semibold text-xs sm:text-sm uppercase tracking-wider">Beneficios</span>
              <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold font-['Outfit'] mt-2 mb-4 sm:mb-6">
                ¿Por qué elegir<br /><span className="text-violet-600">SuperPack</span>?
              </h2>
              <p className="text-zinc-600 text-sm sm:text-lg mb-6 sm:mb-8">
                Somos tu aliado en logística. Comparamos precios, generamos guías y te ayudamos a enviar más barato.
              </p>
              
              <div className="space-y-6">
                {[
                  {
                    icon: <DollarSign className="w-6 h-6" />,
                    title: "Mejor precio garantizado",
                    description: "Buscamos el precio más bajo de cada paquetería para ti.",
                    color: "from-emerald-500 to-teal-500"
                  },
                  {
                    icon: <Zap className="w-6 h-6" />,
                    title: "Express y Terrestre",
                    description: "Elige entre envío rápido o económico según tu necesidad.",
                    color: "from-orange-500 to-amber-500"
                  },
                  {
                    icon: <Shield className="w-6 h-6" />,
                    title: "Envíos protegidos",
                    description: "Todos los envíos incluyen seguro y rastreo en tiempo real.",
                    color: "from-violet-500 to-purple-500"
                  },
                ].map((benefit, index) => (
                  <div key={index} className="flex gap-4 items-start">
                    <div className={`w-12 h-12 bg-gradient-to-br ${benefit.color} rounded-xl flex items-center justify-center text-white shadow-lg flex-shrink-0`}>
                      {benefit.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold mb-1">{benefit.title}</h3>
                      <p className="text-zinc-500">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1758523670564-d1d6a734dc0b?crop=entropy&cs=srgb&fm=jpg&w=800&q=80"
                alt="Package delivery"
                className="rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">+10,000</p>
                    <p className="text-sm text-zinc-500">Envíos exitosos</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="como-funciona" className="py-12 sm:py-24 px-4 bg-gradient-to-br from-violet-600 via-purple-600 to-violet-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55?w=1920')] bg-cover bg-center"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-8 sm:mb-16">
            <span className="text-violet-200 font-semibold text-xs sm:text-sm uppercase tracking-wider">Proceso</span>
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold font-['Outfit'] mt-2 text-white">
              Así de fácil es enviar
            </h2>
            <p className="text-violet-200 mt-2 text-sm sm:text-lg">3 simples pasos y listo</p>
          </div>

          <div className="grid grid-cols-3 gap-3 sm:gap-8">
            {[
              { 
                step: "1", 
                title: "Cotiza", 
                desc: "Ingresa los datos de tu paquete y obtén cotizaciones.",
                icon: <Box className="w-6 h-6 sm:w-10 sm:h-10" />
              },
              { 
                step: "2", 
                title: "Elige", 
                desc: "Selecciona Express o Terrestre según tu necesidad.",
                icon: <Truck className="w-6 h-6 sm:w-10 sm:h-10" />
              },
              { 
                step: "3", 
                title: "Envía", 
                desc: "Genera tu guía y rastrea tu paquete en tiempo real.",
                icon: <CheckCircle className="w-6 h-6 sm:w-10 sm:h-10" />
              }
            ].map((item, index) => (
              <div 
                key={index} 
                className="bg-white/10 backdrop-blur-lg rounded-xl sm:rounded-3xl p-3 sm:p-8 text-center text-white border border-white/20 hover:bg-white/20 transition-all duration-300 animate-fade-in"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="w-12 h-12 sm:w-20 sm:h-20 bg-white/20 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-2 sm:mb-6">
                  {item.icon}
                </div>
                <div className="w-7 h-7 sm:w-10 sm:h-10 bg-white text-violet-600 rounded-full flex items-center justify-center text-sm sm:text-xl font-bold mx-auto mb-2 sm:mb-4">
                  {item.step}
                </div>
                <h3 className="text-base sm:text-2xl font-bold mb-1 sm:mb-3">{item.title}</h3>
                <p className="text-violet-100 text-xs sm:text-base leading-snug">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 sm:py-20 px-4 bg-zinc-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <span className="text-violet-600 font-semibold text-xs sm:text-sm uppercase tracking-wider">Testimonios</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Outfit'] mt-2">
              Empresas que confían en nosotros
            </h2>
            <p className="text-zinc-500 mt-2 text-sm sm:text-base">Miles de negocios mexicanos ya ahorran con SuperPack</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                company: "Liverpool",
                name: "Roberto Martínez",
                role: "Dir. Logística E-commerce",
                text: "Desde que integramos SuperPack, redujimos nuestros costos de envío en un 35%. La comparación automática de paqueterías nos ahorra horas de trabajo.",
                stars: 5
              },
              {
                company: "Mercado Libre",
                name: "Ana García Ramírez",
                role: "Seller Premium",
                text: "Como vendedora en Mercado Libre, SuperPack me permite ser más competitiva. Siempre encuentro el mejor precio y mis clientes reciben rápido.",
                stars: 5
              },
              {
                company: "Coppel",
                name: "Ing. Carlos Mendoza",
                role: "Gerente de Distribución",
                text: "La plataforma es intuitiva y el ahorro es real. Procesamos miles de guías mensuales con SuperPack y la eficiencia mejoró notablemente.",
                stars: 5
              },
              {
                company: "Grupo Bimbo",
                name: "Laura Sánchez Vega",
                role: "Coord. Logística",
                text: "SuperPack nos ayudó a optimizar envíos de muestras y documentos a nivel nacional. El soporte siempre está disponible y el precio es imbatible.",
                stars: 4
              },
              {
                company: "Elektra",
                name: "Miguel Ángel Torres",
                role: "Jefe de Operaciones",
                text: "La integración fue sencilla y los resultados inmediatos. Comparar entre FedEx, DHL y Estafeta en segundos cambió nuestra operación.",
                stars: 5
              },
              {
                company: "Andrea",
                name: "Patricia López Díaz",
                role: "Dir. de E-commerce",
                text: "Nuestros clientes reciben sus pedidos más rápido y a menor costo. SuperPack se convirtió en un aliado estratégico para nuestro e-commerce.",
                stars: 5
              },
            ].map((review, index) => (
              <Card key={index} className="bg-white border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-3">
                    {[...Array(review.stars)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                    {[...Array(5 - review.stars)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-zinc-200" />
                    ))}
                  </div>
                  <p className="text-zinc-700 text-sm leading-relaxed mb-4 italic">
                    &ldquo;{review.text}&rdquo;
                  </p>
                  <div className="flex items-center gap-3 pt-3 border-t border-zinc-100">
                    <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{review.name}</p>
                      <p className="text-xs text-zinc-500">{review.role} - <span className="text-violet-600 font-medium">{review.company}</span></p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 sm:py-24 px-4 bg-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1766040923580-16ad32fae8b4?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80"
            alt="Boxes"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold font-['Outfit'] text-white mb-4 sm:mb-6">
            ¿Listo para enviar más barato?
          </h2>
          <p className="text-zinc-400 mb-6 sm:mb-10 text-sm sm:text-xl">
            Únete a miles de negocios que ya ahorran con SuperPack
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <Button 
              onClick={() => navigate("/register")}
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 px-6 py-5 sm:px-10 sm:py-7 text-base sm:text-lg font-bold shadow-2xl shadow-violet-500/30"
              data-testid="final-cta-btn"
            >
              Comenzar ahora - Es gratis
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="border-2 border-zinc-700 text-white hover:bg-zinc-800 px-6 py-5 sm:px-10 sm:py-7 text-base sm:text-lg font-semibold"
            >
              Probar cotizador
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-950 text-zinc-400 py-10 sm:py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-10 mb-8 sm:mb-10">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-purple-600 rounded-xl flex items-center justify-center">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <span className="text-white font-bold font-['Outfit'] text-xl">SuperPack</span>
              </div>
              <p className="text-sm">El mejor comparador de envíos de México.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Producto</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Cotizador</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Paqueterías</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Precios</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Empresa</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/sobre-nosotros" className="hover:text-white transition-colors">Nosotros</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="/contacto" className="hover:text-white transition-colors">Contacto</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Términos</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacidad</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-zinc-800 pt-8 text-center text-sm">
            <p>© 2026 SuperPack.mx - Todos los derechos reservados</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
